import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators, compose } from "redux";
import _ from "lodash";
import { Grid } from "@material-ui/core";
import { InputText, InputTypeAhead } from "@loanbossinc/component-library";
import SimpleConfirmDialog from "components/Global/ConfirmationDialogs/SimpleConfirmDialog";

import { actionCreators as drawerActionCreators } from "../lenderReportingDrawer.store";
import { actionCreators as lineItemActionCreators } from "../line-item-management/lineItemManagement.store";
import { getReportGroupsForDeal } from "../../dashboard/dashboard.store";
import { setNoiCalculationsDashboardSelections } from "../../reportingComponents/noiCalculationDashboard/noiRegularCalculationDashboard/noiRegularCalculationDashboard.store";
import { setDscrLineItemSelections } from "../dscr-line-items-management/dscrLineItemManagement.store";
import { actionCreators as fieldManagementActionCreators } from "./fieldManagement.store";
import { actionCreators as rentRollFieldActionCreators } from "./rent-roll-field/rentRollField.store";
import CreateNewField from "./createNewField";
import FieldListItem from "./fieldListItem";
import { PaddedWrapper, Row, StyledSecondaryButton } from "./styles";

const FieldManagement = props => {
  const {
    deal,
    fields,
    fieldsFilter,
    getFields,
    isAdding,
    isEditing,
    isFromDebtYieldCalculation,
    isOpen,
    onAdd = _.noop,
    setFieldSelections,
    isFromAddNewLineItem,
    newLineItemType,
    isMultipleProperties,
    setDrawerSelections,
    setShowLabeledButton,
    isFromFile = false,
    isLabeledButtonShow,
    setDebtYieldCalculationDashboardSelections,
    setDebtYieldSetupSelections,
    debtYieldCalculations,
    activeLoanId,
    deleteFieldConfig,
    deleteDialogOpen,
    fieldIdToDelete,
    fieldToDelete,
    isChoosingForAccountingIntegration,
    onChooseForAccountingIntegration,
    accountingIntegrations,
    reportConfigs,
    propertyId,
    getAllFilesForDeal,
    allFilesForDeal,
    updateFieldsList,
    resetFieldsList,
    fieldDeleted,
    getNoiValuesPerGroup,
    noiValuesPerReportGroup,
    wasNoiDashboardUpdated
  } = props;

  const [searchTerm, setSearchTerm] = useState("");
  const [searchFieldIsDisabled, setSearchFieldIsDisabled] = useState(true);

  useEffect(() => {
    if (!reportConfigs) {
      getReportGroupsForDeal({ dealId: deal.id });
    }
    if (!noiValuesPerReportGroup) {
      getNoiValuesPerGroup({ dealId: deal.id });
    }
    setFieldsFilter(propertyId || "0");
  }, []);

  useEffect(() => {
    if (isOpen && !fields) {
      getFields({ dealId: deal.id });
      if(updateFieldsList) {
        updateFieldsList();
      }
    }
    if (!isOpen) {
      setFieldSelections({
        fields: null,
        isAdding: false,
        isEditing: false,
        currentSelectedFieldType: null,
        isFromAddNewLineItem: false,
        isFromDebtYieldCalculation: false,
        isSelectingField: false,
        selectedField: null,
        noiValuesPerReportGroup: null
      });
      resetFieldsList && resetFieldsList();
    }
    if (fieldDeleted) {
      updateFieldsList();
      setFieldSelections({fieldDeleted: false});
    }

    if (fields) {
      setSearchFieldIsDisabled(false);
    }
  }, [deal, fields, isOpen, resetFieldsList]);

  useEffect(() => {
    if (!allFilesForDeal) {
      getAllFilesForDeal({ dealId: deal.id });
    }
  }, [allFilesForDeal]);

  useEffect(() => {
    if (isFromFile) {
      setFieldSelections({
        currentSelectedFieldType: "file"
      });
    }
  }, [isFromFile, isLabeledButtonShow]);

  const onAddNew = () => {
    setFieldSelections({ isAdding: true });
    if (isFromFile) {
      setShowLabeledButton(true);
    }
    if (onAdd && typeof onAdd === "function") {
      onAdd();
    }
  };

  const handleSearch = event => {
    event.persist();
    const debounceDelay = 500;
    const handleChangeDebounce = _.debounce(e => {
      if (e.target.value) {
        setSearchTerm(e.target.value);
      } else {
        setSearchTerm("");
      }
    }, debounceDelay);

    handleChangeDebounce(event);
  };

  const editField = field => {
    let selectedFieldType;
    if (isFromFile) {
      const { value, name } = field;
      const { lineItemType } = props;
      const sourceFileObj = allFilesForDeal.find(item => item.id === field.sourceFileId);
      const updatedTotalValuesForLoans =
        debtYieldCalculations &&
        debtYieldCalculations.map(calc => {
          if (calc.id !== activeLoanId) {
            return calc;
          }
          let updatedLineItem = {
            lineItemType,
            propertyName: name,
            fieldId: field.id,
            value,
            currentSelectedFieldType: getFieldTypeNameByFieldTypeFlags(field)
          }
          if(field.isFromFile){
            updatedLineItem = { ...updatedLineItem,
              sourceName: sourceFileObj.sourceName,
              fileTypeName: sourceFileObj.typeName,
              fileCreatedAt: sourceFileObj.createdAt
            };
          }
          return { ...calc, lineItem: updatedLineItem };
        });
      setDebtYieldSetupSelections({ debtYieldCalculations: updatedTotalValuesForLoans, loanDebtYieldTotal: value });
      setDebtYieldCalculationDashboardSelections({ isLenderReportingDrawerOpen: false, initialDrawerSectionName: null });
    } else {
      setFieldSelections({ isEditing: true, selectedField: field, currentSelectedFieldType: getFieldTypeNameByFieldTypeFlags(field) });
    }
  };

  const getFieldTypeNameByFieldTypeFlags = (field) => {
      if (field.isHardCoded) {
        return "hard-coded";
      } else if (field.isCalculated) {
        return "calculated";
      } else if (field.isFromFile) {
        return "file";
      } else if (field.isBalanceOverride || field.isFinanceOverride) {
        return "finance_override";
      } else if (field.isFromLoanBoss) {
        return "loanboss";
      } else if (field.isFromRentRoll) {
        return "rentroll";
      } else {
        throw new Error("Invalid field source");
      }
  };

  const mapFieldsToListItems = fieldList => fieldList.map(field => (
    <FieldListItem
      key={field.id}
      onClick={editField}
      deal={deal}
      field={field}
      isFromFile={isFromFile}
      setFieldSelections={setFieldSelections}
      isChoosingForAccountingIntegration={isChoosingForAccountingIntegration}
      onChooseForAccountingIntegration={onChooseForAccountingIntegration}
      accountingIntegrations={accountingIntegrations}
      wasNoiDashboardUpdated={wasNoiDashboardUpdated}
    />
  ));

  const getFilteredFields = () => {
    if (!fields) {
      return [];
    }

    let filteredFields = fields.filter(field => {
      if (isFromFile) {
        return (field.type === "file" || field.type === "calculated");
      }

      return (
        !fieldsFilter ||
        fieldsFilter === "0" ||
        (fieldsFilter === "-2" && field.level === "all") ||
        (fieldsFilter === "-1" && field.level === "deal") ||
        (field.level === "property" && Boolean(field.propertyId) && field.propertyId.toString() === fieldsFilter)
      );
    });

    if (!_.isEmpty(searchTerm)) {
      filteredFields = filteredFields.filter(field => field && field.name && field.name.toLowerCase().includes(searchTerm.toLowerCase()));
    }

    if (props.isChoosingForAccountingIntegration) {
      filteredFields = filteredFields.filter(field => field.isFromFile || field.isFromRentRoll || field.isCalculated);
    }

    return mapFieldsToListItems(_.sortBy(filteredFields, ["name"]));
  };

  const setFieldsFilter = value => {
    setFieldSelections({ fieldsFilter: value });
  };

  const propertySummaries = _.sortBy(Boolean(deal) && Boolean(deal.properties) && deal.properties.map(x => ({ id: x.id.toString(), name: x.name }), ["name"]));
  const sourceFilters = [
    { id: "0", name: "All fields" },
    { id: "-1", name: "Deal-level fields" },
    { id: "-2", name: "Hard-coded fields" },
    ...propertySummaries
  ];

  // check if needs to be redirected back to "add new list item" drawer state after saving new field
  const changeDrawerStateIfInNewListItemMode = useCallback(() => {
    if (isFromAddNewLineItem) {
      let redirectToDrawerState;
      if (isMultipleProperties) {
        redirectToDrawerState = "comparison-field-selection";
      } else {
        redirectToDrawerState = newLineItemType === "value" ? "line-item-management" : "comparison-field-selection";
      }
      setDrawerSelections({ currentSectionName: redirectToDrawerState });
    }
  }, []);

  const handleDeleteField = () => {
    deleteFieldConfig({ fieldId: fieldIdToDelete });
    getFields({ dealId: deal.id });
  };

  return (
    <>
      {/* List fields filter and search */}
      {!isAdding && !isEditing && (
        <Row>
          <Grid container>
            <Grid item xs={isFromFile ? 10 : 5}>
              <PaddedWrapper>
                <InputText
                  disabled={searchFieldIsDisabled}
                  onChange={handleSearch}
                  variant="filled"
                  id="search-field-by-name"
                  name="search-field-by-name"
                  label="Search a field by name"
                  fullWidth
                />
              </PaddedWrapper>
            </Grid>
            {!isFromFile && (
              <Grid item xs={5}>
                <PaddedWrapper padding="0 1em 0 0">
                  <InputTypeAhead
                    variant="filled"
                    id="filter"
                    name="filter"
                    label="Filter by"
                    options={sourceFilters}
                    displayWith={option => option && option.name}
                    setValueWith={option => option && option.id}
                    onChange={setFieldsFilter}
                    value={fieldsFilter}
                    style={{ width: "auto" }}
                  />
                </PaddedWrapper>
              </Grid>
            )}
            <Grid item xs={2}>
              <PaddedWrapper padding="0.25em 1em 0 0">
                <StyledSecondaryButton id="add-new-field" onClick={onAddNew} label="Add New" style={{ width: "auto" }} />
              </PaddedWrapper>
            </Grid>
          </Grid>
        </Row>
      )}
      {!isAdding && !isEditing ? (
        getFilteredFields()
      ) : (
        <CreateNewField
          {...props}
          dealId={deal?.id || null}
          changeDrawerStateIfInNewListItemMode={changeDrawerStateIfInNewListItemMode}
          isFromDebtYieldCalculation={isFromDebtYieldCalculation}
        />
      )}

      <SimpleConfirmDialog
        modalOpen={deleteDialogOpen}
        title={wasNoiDashboardUpdated ? "Unsaved Changes" : "Delete Field"}
        message={getDeleteMessageText(fieldToDelete, wasNoiDashboardUpdated)}
        handleYes={wasNoiDashboardUpdated ? () => setFieldSelections({ deleteDialogOpen: false }) : handleDeleteField}
        handleNo={() => setFieldSelections({ deleteDialogOpen: false })}
        yesText={wasNoiDashboardUpdated ? "Okay" : "Delete"}
        noText="Cancel"
        excludeNoText={wasNoiDashboardUpdated}
      />
    </>
  );
};

const mapStateToProps = ({
  lenderReportingDrawer,
  lenderReportFieldManagement,
  lenderReportNewLineItemManagement: { newLineItemType, isMultipleProperties },
  lenderReportRentRollField: { isStep2Screen, showConfirmationModal, locationModalIsOpenFrom, isAnythingWasUpdated },
  lenderReportList: { reportConfigs },
  lenderReportDscrLineItemManagement: {selectedDscrType},
  authentication
}) => ({
  ...lenderReportingDrawer,
  ...lenderReportFieldManagement,
  newLineItemType,
  isMultipleProperties,
  isStep2Screen,
  reportConfigs,
  authentication,
  showConfirmationModal,
  locationModalIsOpenFrom,
  isAnythingWasUpdated,
  selectedDscrType
});
const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      ...drawerActionCreators,
      ...fieldManagementActionCreators,
      ...rentRollFieldActionCreators,
      ...lineItemActionCreators,
      getReportGroupsForDeal,
      setNoiCalculationsDashboardSelections,
      setDscrLineItemSelections
    },
    dispatch
  );

function getDeleteMessageText(field, wasNoiDashboardUpdated) {
  if (!field) {
    return null;
  }
  if (wasNoiDashboardUpdated) {
    return (
      "You have unsaved changes on this page. You must click 'Save' or 'Cancel changes' before you can delete this field."
    );
  } else if (field.isUsedInCalculatedField) {
    return (
      `You are about to delete a field that is used within a calculated field. ` +
      `If you delete this field you will also delete the calculated field and any associated line item(s).`
    );
  } else if (field.isUsedInLineItem) {
    return (
      `You are about to delete a field that is used as a line item within your calculation. ` +
      `If you delete this field you will also delete the associated line item(s).`
    );
  } else {
    return "Are you sure you want to delete this field?";
  }
}

export default compose(connect(mapStateToProps, mapDispatchToProps))(FieldManagement);
